import { Component } from 'react'
import {
  Router,
  ShippingOperationConfig,
  RemoteOperation,
  getDataFromSchema,
  FormSchema,
  FormSchemaData
} from 'stylewhere/shared'
import { isModalError, showToastError } from 'stylewhere/utils'
import { Shippings, ShippingParcel, Asn, Asns, Parcels } from 'stylewhere/api'
import type { Routes } from 'stylewhere/pages'
import { T, __ } from 'stylewhere/i18n'
import { Box, Icons, OperationStart, Page } from 'stylewhere/components'
import { ShippingExtensions } from 'stylewhere/extensions'
import { get as _get, set as _set } from 'lodash'
import { CustomError } from 'stylewhere/shared/errors'

export interface ShippingParcelAddParams {
  opCode: string
  asnId: string
  parcelId: string
}

interface State {
  asn?: Asn | undefined
  parcel?: ShippingParcel | undefined
  loading: boolean
  initialValues?: object
}

interface LocationState {
  asn?: Asn
  parcel?: ShippingParcel
  filterParams?: Record<string, any>
}

interface ShippingParcelAddProps {
  match: { params: ShippingParcelAddParams }
  location: { state?: LocationState }
  history: any
}

export default class ShippingParcelAdd extends Component<ShippingParcelAddProps, State> {
  asnId: string
  parcelId: string
  opCode: string
  isModal: boolean
  operation: ShippingOperationConfig
  submitPath: Routes = '/shipping/:opCode/reading'
  submitPathEncoding: Routes = '/shipping/withEncoding/:opCode/reading'
  backPath: Routes = '/shipping/:opCode/asn/:asnId'
  submitInProgress: boolean = false
  state: State = {
    loading: true
  }
  get locationState(): LocationState | undefined {
    return this.props.location.state
  }

  constructor(props: any) {
    super(props)
    const matchParams: ShippingParcelAddParams = this.props.match.params
    this.asnId = matchParams.asnId
    this.parcelId = matchParams.parcelId
    this.opCode = matchParams.opCode
    this.isModal = false
    this.operation = RemoteOperation.getOperationConfig<ShippingOperationConfig>(this.opCode)
  }

  componentDidMount() {
    this._init()
  }

  _init = async () => {
    this.isModal = isModalError(this.operation)
    try {
      let asn: State['asn'], parcel: State['parcel']
      if (this.locationState?.asn) {
        asn = this.locationState?.asn
      } else {
        const loadedAsn = await Asns.get<Asn>(this.asnId)
        if (loadedAsn) {
          asn = loadedAsn
        }
      }
      if (!asn) {
        showToastError(__(T.misc.shipment), __(T.error.error), this.isModal)
        Router.navigate(Router.replaceMatchParams(this.backPath, { asnId: this.asnId }), {
          opCode: this.operation.code
        })
        return
      }

      const schema = ShippingExtensions.formSchema(this.operation, undefined, ['', 'parcel'])

      // parcel id is not always available, when we add a new parcel it should be empty
      if (this.locationState?.parcel) {
        parcel = this.locationState?.parcel
      } else if (this.parcelId) {
        const loadedParcel = await Parcels.get<ShippingParcel>(this.parcelId)
        if (loadedParcel) parcel = loadedParcel
      }

      const initialValues = await Shippings.getInitialValueParcel(schema, asn, parcel)
      if (parcel) {
        this.setState({ asn, parcel, initialValues }, this.checkAutoSubmit)
      } else {
        this.setState({ asn, loading: false, initialValues })
      }
    } catch (error) {
      showToastError(error, __(T.error.error), this.isModal)
      this.setState({ loading: false })
    }
  }

  checkAutoSubmit = () => {
    const { initialValues } = this.state
    const schema = ShippingExtensions.formSchema(this.operation, undefined, ['', 'parcel'])
    let directSubmit = schema
      ? schema.filter((s) => s.readOnly === true || s.readOnly === 'read' || s.type === 'hidden').length ===
        schema.length
      : false
    if (directSubmit) {
      // si verifica che ci siano tutti i dati richiesti dal formSchema
      schema?.forEach((s) => {
        const value = _get(initialValues, s.name)
        if (!value) {
          directSubmit = false
        }
      })
    }
    if (directSubmit) {
      this.onSubmit(initialValues, this.operation, schema)
    } else {
      this.setState({ loading: false })
    }
  }

  onSubmit = async (formData: FormSchemaData, operation: ShippingOperationConfig, schema: FormSchema) => {
    if (this.submitInProgress) return

    const { asn, parcel: selectedParcel } = this.state
    if (!asn) {
      showToastError(__(T.misc.shipment), __(T.error.error), this.isModal)
      return
    }

    this.submitInProgress = true

    let parcel = selectedParcel
    const payloadStartParcel: any = getDataFromSchema(formData, schema) || {}
    const payload = {
      ...payloadStartParcel,
      asn: { code: asn.code }
    }

    try {
      parcel = await Shippings.startParcel(operation, payload)

      if (!formData.parcelCode) {
        formData.parcelCode = parcel.code
      }
      Router.navigate(
        this.operation.templatePath === 'withEncoding' ? this.submitPathEncoding : this.submitPath,
        { opCode: operation.code, asnId: this.asnId },
        {
          state: {
            formData,
            parcel,
            asn,
            filterParams: this.locationState?.filterParams ?? {},
            backRoute: this.backPath,
            backRouteParams: {
              opCode: this.opCode,
              asnId: this.asnId
            }
          }
        }
      )
    } catch (err) {
      this.submitInProgress = false
      if (err instanceof CustomError) {
        showToastError(
          `${CustomError.interpolateMessage(err.message, payload)}. [${err.errorCode}]`,
          __(T.error.error),
          this.isModal
        )
        return
      }
      showToastError(err, 'Start Parcel Error', this.isModal)
      this.setState({ loading: false })
    }
  }

  render() {
    const { loading, initialValues } = this.state
    if (loading) {
      return (
        <Page
          title={__(T.misc.loading)}
          onBackPress={() => {
            Router.navigate(this.backPath, { opCode: this.opCode, asnId: this.asnId })
          }}
        >
          <Page.Content notBoxed>
            <Box flex center>
              <Icons.Loader1 />
            </Box>
          </Page.Content>
        </Page>
      )
    }

    const filterParams = this.locationState?.filterParams ?? {}
    return (
      <OperationStart
        backPath={Router.replaceMatchParams(this.backPath, { asnId: this.asnId, ...filterParams })}
        startWithRfidSearch={false}
        submitPath={this.submitPath}
        extensions={ShippingExtensions}
        onSubmit={this.onSubmit}
        initialValues={initialValues}
        filterKey={['', 'parcel']}
      />
    )
  }
}
